<template>
  <div
    class="select relative"
    :class="{'select--disabled': isDisabled}"
  >
    <input
      :id="inputId"
      v-model="selectedInternal.id"
      type="hidden"
      :name="inputName"
    >

    <button
      :id="toggleId"
      type="button"
      class="select__toggle"
      :class="{'select__toggle--active': isActive}"
      aria-haspopup="true"
      :aria-controls="dropdownId"
      :disabled="isDisabled"
      @click="toggleSelect"
    >
      <span class="select__dropdown-text">{{ selectionMessage }}</span>
      <i class="drop-arrow drop-arrow--end arrow-svg arrow-svg--grey" />
    </button>

    <ul
      v-show="isActive"
      :id="dropdownId"
      role="radiogroup"
      class="select__dropdown"
    >
      <li
        v-for="option in options"
        :key="option.id"
        class="select__option"
      >
        <label
          class="select__label no-margin"
          :for="getOptionInputId(option)"
        >
          <input
            :id="getOptionInputId(option)"
            v-model="selectedInternal"
            class="select__input"
            type="radio"
            :name="dropdownOptionsName"
            :value="option"
          >
          <span class="select__label-text">{{ option.name }}</span>
        </label>
      </li>
    </ul>
  </div>
</template>

<script>
import './select.scss'

import mixinPopupCloseListeners from '../../mixins/mixin-popup-close-listeners'
import mixinFocusCapture from '../../mixins/mixin-focus-capture'

const UNDEFINED_ID = '__UNDEFINED__'
const UNDEFINED_OBJECT = { id: UNDEFINED_ID, name: 'None' }
const DEFAULT_SELECT_MESSAGE = 'Select option'

export default {
  name: 'VSelect',

  mixins: [
    mixinPopupCloseListeners({closeCallback: 'closeSelect'}),
    mixinFocusCapture({toggleVariable: 'isActive', closeCallback: 'closeSelect', openCallback: 'openSelect'})
  ],

  props: {
    inputId: {
      required: true,
      type: String
    },
    inputName: {
      default: '',
      type: String
    },
    options: {
      default: () => [], // [{ id: , name: }]
      type: Array
    },
    selected: {
      type: Object,
      default: () => UNDEFINED_OBJECT
    }
  },

  data () {
    return {
      isActive: false,
      selectedInternal: null,
      dropdownId: 'v-select-dropdown-' + this.inputId,
      dropdownOptionsName: 'v-select-dropdown-input' + this.inputId,
      toggleId: 'v-select-toggle-' + this.inputId,

      mixinModalId: 'v-select-dropdown-' + this.inputId,
      mixinTriggerId: 'v-select-toggle-' + this.inputId,
      mixinIsRadioGroup: true
    }
  },

  computed: {
    isDisabled () {
      return !this.options.length
    },

    selectionMessage () {
      return this.selectedInternal.id === UNDEFINED_ID ? DEFAULT_SELECT_MESSAGE : this.selectedInternal.name
    }
  },

  watch: {
    selected (newSelectedOption) {
      this.selectedInternal = newSelectedOption === null ?
        UNDEFINED_OBJECT :
        newSelectedOption
    },

    selectedInternal (newSelectedInternal) {
      this.$emit('update:selected-option', newSelectedInternal.id)
    }
  },

  created () {
    this.initializeSelectedInternal()
    this.$eventHub.$on('resetForm', this.reset)
    this.$eventHub.$on('resetSelect', this.reset)
  },

  methods: {
    closeSelect () {
      this.isActive = false
    },

    openSelect () {
      this.isActive = true
    },

    toggleSelect (e) {
      if (this.options.length && !this.isActive) {
        this.openSelect(e)
      } else {
        this.closeSelect(e)
      }
    },

    initializeSelectedInternal () {
      if (this.selected === null) {
        this.selectedInternal = UNDEFINED_OBJECT
      } else {
        this.selectedInternal = this.selected
      }
    },

    isSelected (option) {
      return option.id === this.selectedInternal.id
    },

    getOptionInputId (option) {
      return `option-${this.inputId}-${option.id}`
    },

    reset () {
      this.selectedInternal = UNDEFINED_OBJECT
    }
  }
}
</script>
